import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { BRAND_ORANGE } from '../../../styles/portalTheme';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useEventQuery } from '../../../api/use-event-query';
import { useSubmitPartnerEventMutation } from '../../../api/use-submit-partner-event-mutation';
import { useEventsScope } from '../../../contexts/EventsScopeContext';
import ScrollableContentWithHint from '../../Events/widgets/ScrollableContentWithHint';
import { getEventModerationRecords } from '../../Events/eventModerationRecords';
import OnlineEventLocationDisplay from '../../Events/widgets/OnlineEventLocationDisplay';
import {
  isOnlineEventFormat,
  resolveEventFormatFromApi,
  resolveOnlineMeetingLinkFromApi,
} from '../../Events/ManageEvent/eventFormatConstants';
import EventModerationTimeline from './eventModerationTimeline';
import { formatTimelineTimestamp } from '../../Events/widgets/timelineDateUtils';
import {
  EVENT_DESCRIPTION_MAX,
  EVENT_DESCRIPTION_MIN_HEIGHT,
  EVENT_DESCRIPTION_MIN_ROWS,
  EVENT_SHORT_DESCRIPTION_MAX,
  SCROLLABLE_DESCRIPTION_SX,
  normalizeMultilineText,
} from '../../Events/ManageEvent/eventFormConstants';
import { characterCountSx } from '../../Events/ManageEvent/components/manageEventFieldStyles';

const PAGE_CARD_SHADOW = '0px 0px 10px rgba(0,0,0,0.06)';

const FIELD_CHAR_LIMITS = {
  description: EVENT_DESCRIPTION_MAX,
  wingmanDescription: EVENT_SHORT_DESCRIPTION_MAX,
};

const CharacterCount = ({ current, max }) => (
  <Typography component="span" sx={{ ...characterCountSx, display: 'block', mt: 0.5 }}>
    {current}/{max}
  </Typography>
);

const normalize = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const getSeverityColor = (severity) => {
  const value = normalize(severity);
  if (value === 'high') {
    return {
      rail: '#e06a3f',
      badgeBg: '#fdf1ed',
      badgeColor: '#c56f4f',
      actionColor: '#d85d4a',
      actionLabel: 'Action Required',
    };
  }
  if (value === 'medium') {
    return {
      rail: '#2b7de9',
      badgeBg: '#e8f1ff',
      badgeColor: '#2b7de9',
      actionColor: '#2b7de9',
      actionLabel: 'Recommended',
    };
  }
  return {
    rail: '#98a2b3',
    badgeBg: '#f2f4f7',
    badgeColor: '#475467',
    actionColor: '#667085',
    actionLabel: 'Recommended',
  };
};

const toDisplayFieldName = (field) =>
  String(field || 'general')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const toMessageLines = (message) => {
  const text = String(message || '').trim();
  if (!text) return [];
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
};

const toTitleCase = (value) => {
  const text = String(value || '')
    .trim()
    .toLowerCase();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const normalizeModerationPrice = (value) => {
  const trimmed = String(value ?? '')
    .trim()
    .replace(/\s*AED$/i, '');
  if (!trimmed) return '';
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? String(parsed) : trimmed;
};

const normalizeModerationSeats = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? String(parsed) : trimmed;
};

const formatPriceDisplay = (event) => {
  const raw = event?.price;
  const currency = event?.currency ?? 'AED';
  if (raw == null || raw === '') return '—';
  if (typeof raw === 'number') return `${raw} ${currency}`;
  const trimmed = String(raw).trim();
  return new RegExp(currency, 'i').test(trimmed) ? trimmed : `${trimmed} ${currency}`;
};

const getRawPriceValue = (event) => {
  const raw = event?.price;
  const currency = event?.currency ?? 'AED';
  if (raw == null || raw === '') return '';
  if (typeof raw === 'number') return String(raw);
  return String(raw)
    .trim()
    .replace(new RegExp(`\\s*${currency}$`, 'i'), '');
};

const getRawTotalSeatsValue = (event) => {
  const value = event?.totalSeats;
  if (value == null || value === '') return '';
  return String(value);
};

const FIELD_PREVIEW_CONFIG = {
  title: {
    label: 'Event Title',
    getValue: (event) => event?.title || '—',
    getEditableValue: (event) => event?.title ?? '',
  },
  description: {
    label: 'Description',
    getValue: (event) => {
      const text = normalizeMultilineText(event?.description ?? '');
      return String(text).trim() || '—';
    },
    getEditableValue: (event) =>
      normalizeMultilineText(event?.description ?? '').slice(0, EVENT_DESCRIPTION_MAX),
  },
  wingmanDescription: {
    label: 'Short Description',
    getValue: (event) => event?.wingmanDescription ?? '—',
    getEditableValue: (event) =>
      String(event?.wingmanDescription ?? '').slice(0, EVENT_SHORT_DESCRIPTION_MAX),
  },
  venueName: {
    label: 'Venue Name',
    getValue: (event) => event?.venueName ?? '—',
    getEditableValue: (event) => event?.venueName ?? '',
  },
  onlineMeetingLink: {
    label: 'Online event',
    getValue: (event) => resolveOnlineMeetingLinkFromApi(event) || '—',
    getEditableValue: (event) => resolveOnlineMeetingLinkFromApi(event),
  },
  price: {
    label: 'Price (in AED)',
    getValue: (event) => formatPriceDisplay(event),
    getEditableValue: (event) => getRawPriceValue(event),
  },
  totalSeats: {
    label: 'Total Seats',
    getValue: (event) => {
      const value = event?.totalSeats;
      if (value == null || value === '') return '—';
      return String(value);
    },
    getEditableValue: (event) => getRawTotalSeatsValue(event),
  },
};

/** Aligns admin feedback `field` keys with partner preview / resubmit payload keys. */
const FEEDBACK_FIELD_ALIASES = {
  longDescription: 'description',
  long_description: 'description',
  wingman_description: 'wingmanDescription',
  shortDescription: 'wingmanDescription',
  short_description: 'wingmanDescription',
  venue_name: 'venueName',
  location: 'venueName',
  online_meeting_link: 'onlineMeetingLink',
  meeting_link: 'onlineMeetingLink',
  meetingLink: 'onlineMeetingLink',
  total_seats: 'totalSeats',
  seats_total: 'totalSeats',
  ticket_price: 'price',
};

const toCanonicalFieldKey = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  if (FIELD_PREVIEW_CONFIG[trimmed]) return trimmed;

  const normalized = trimmed.toLowerCase().replace(/\s+/g, '_');
  return (
    FEEDBACK_FIELD_ALIASES[trimmed] ||
    FEEDBACK_FIELD_ALIASES[normalized] ||
    FEEDBACK_FIELD_ALIASES[trimmed.toLowerCase()] ||
    trimmed
  );
};

const resolveLocationPreviewKey = (canonicalKey, event) => {
  const isOnline = isOnlineEventFormat(resolveEventFormatFromApi(event));
  if (isOnline && canonicalKey === 'venueName') return 'onlineMeetingLink';
  if (!isOnline && canonicalKey === 'onlineMeetingLink') return null;
  return canonicalKey;
};

const getPreviewFieldByFeedbackKey = (key, event) => {
  const canonicalKey = toCanonicalFieldKey(key);
  const previewKey = resolveLocationPreviewKey(canonicalKey, event);
  if (!previewKey) return null;

  const config = FIELD_PREVIEW_CONFIG[previewKey];
  if (!config) return null;
  return {
    key: previewKey,
    label: config.label,
    value: config.getValue(event),
    editableValue: config.getEditableValue?.(event) ?? config.getValue(event),
  };
};

const mapPreviewFieldToApiChangeKey = {
  title: 'title',
  description: 'description',
  wingmanDescription: 'wingmanDescription',
  venueName: 'venueName',
  onlineMeetingLink: 'onlineMeetingLink',
  price: 'price',
  totalSeats: 'totalSeats',
};

const PartnerModerationFeedback = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { routes } = useEventsScope();
  const { data, isLoading, isError, error } = useEventQuery(eventId);
  const submitEventMutation = useSubmitPartnerEventMutation({
    onSuccess: (payload) => {
      toast.success(payload?.message || 'Event re-submitted for moderation review');
      navigate(routes.detail(eventId));
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to re-submit event');
    },
  });

  const latestModeration = useMemo(() => getEventModerationRecords(data)[0] ?? null, [data]);
  const feedback = latestModeration?.feedback ?? {};
  const feedbackItems = useMemo(
    () => (Array.isArray(feedback.items) ? feedback.items : []),
    [feedback.items],
  );
  const previewFields = useMemo(() => {
    const seen = new Set();
    const fromFeedback = feedbackItems
      .map((item) => getPreviewFieldByFeedbackKey(item?.field, data))
      .filter((field) => {
        if (!field || field.key === 'price') return false;
        if (seen.has(field.key)) return false;
        seen.add(field.key);
        return true;
      });

    if (fromFeedback.length) return fromFeedback;

    if (!feedbackItems.length) return [];

    return Object.entries(FIELD_PREVIEW_CONFIG)
      .filter(([key]) => key !== 'price')
      .map(([key, config]) => ({
        key,
        label: config.label,
        value: config.getValue(data),
        editableValue: config.getEditableValue?.(data) ?? config.getValue(data),
      }));
  }, [feedbackItems, data]);
  const [isPreviewEditable, setIsPreviewEditable] = useState(false);
  const [editableFieldValues, setEditableFieldValues] = useState({});
  const initialEditableValues = useMemo(() => {
    const initialValues = {};
    previewFields.forEach((field) => {
      initialValues[field.key] = field.editableValue ?? field.value ?? '';
    });
    return initialValues;
  }, [previewFields]);

  useEffect(() => {
    setEditableFieldValues(initialEditableValues);
  }, [initialEditableValues]);

  const handleEditableFieldChange = (fieldKey, rawValue) => {
    const maxLength = FIELD_CHAR_LIMITS[fieldKey];
    const value =
      maxLength != null ? String(rawValue ?? '').slice(0, maxLength) : String(rawValue ?? '');

    setEditableFieldValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleResubmitForApproval = () => {
    for (const [fieldKey, maxLength] of Object.entries(FIELD_CHAR_LIMITS)) {
      if (!(fieldKey in editableFieldValues)) continue;

      const length = String(editableFieldValues[fieldKey] ?? '').trim().length;
      if (length > maxLength) {
        const label = FIELD_PREVIEW_CONFIG[fieldKey]?.label ?? fieldKey;
        toast.error(`${label} must be ${maxLength} characters or fewer.`);
        return;
      }
    }

    const changes = {};

    Object.entries(editableFieldValues).forEach(([previewKey, value]) => {
      const initialValue = String(initialEditableValues?.[previewKey] ?? '').trim();
      const currentValue = String(value ?? '').trim();
      const apiKey = mapPreviewFieldToApiChangeKey[previewKey];
      if (!apiKey) return;

      if (apiKey === 'price') {
        if (normalizeModerationPrice(currentValue) === normalizeModerationPrice(initialValue)) {
          return;
        }
        changes[apiKey] = normalizeModerationPrice(currentValue);
        return;
      }

      if (apiKey === 'totalSeats') {
        if (normalizeModerationSeats(currentValue) === normalizeModerationSeats(initialValue)) {
          return;
        }
        changes[apiKey] = normalizeModerationSeats(currentValue);
        return;
      }

      if (currentValue === initialValue) return;
      changes[apiKey] = currentValue;
    });

    if (!Object.keys(changes).length) {
      toast.error('Please update at least one field before re-submitting.');
      return;
    }

    submitEventMutation.mutate({
      eventId,
      payload: {
        status: 'resubmit',
        changes,
      },
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          {error?.message || 'Event not found'}
        </Typography>
        <Button
          onClick={() => navigate(routes.detail(eventId))}
          sx={{ color: BRAND_ORANGE, textTransform: 'none' }}
        >
          Back to Event
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
        <Button
          startIcon={<ArrowBackIosIcon sx={{ fontSize: 14 }} />}
          onClick={() => navigate(routes.detail(eventId))}
          sx={{
            color: BRAND_ORANGE,
            textTransform: 'none',
            p: 0,
            minWidth: 'auto',
            fontWeight: 500,
            '& .MuiButton-startIcon': { mr: -0.2, ml: -0.35 },
          }}
        >
          Back
        </Button>
        <Typography
          sx={{ fontSize: '1.25rem', lineHeight: 1.15, fontWeight: 700, color: 'text.primary' }}
        >
          Moderation Feedback
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'primary.light',
          bgcolor: 'primary.light',
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mt: 0.1,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 500, lineHeight: 1.25 }}>
              Review Required Before Publishing
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.45, color: 'text.primary' }}>
              Our moderation team has reviewed your event and identified a few areas that need
              attention before we can approve it for publication. Please review the feedback below
              and make the necessary updates.
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.45, color: 'text.primary' }}>
              Once you've addressed these items, resubmit your event for final approval.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={2.25} alignItems="flex-start">
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2, borderRadius: 2.5, boxShadow: PAGE_CARD_SHADOW }}>
            <Typography sx={{ fontWeight: 600, mb: 1.25, fontSize: '1.15rem', lineHeight: 1.2 }}>
              Moderation Comments
            </Typography>
            <Box sx={{ py: 1.5, borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar
                  src={latestModeration?.requestedByUser?.avatarUrl || ''}
                  alt={latestModeration?.requestedByUser?.fullName || 'Admin'}
                  sx={{ width: 34, height: 34 }}
                >
                  {(latestModeration?.requestedByUser?.fullName || 'A').charAt(0).toUpperCase()}
                </Avatar>
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.4, fontSize: '0.82rem' }}>
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {latestModeration?.requestedByUser?.fullName || 'Moderation Team'}
                  </Box>
                  {' · '}
                  {formatTimelineTimestamp(
                    latestModeration?.requestedAt || latestModeration?.updatedAt,
                  )}
                </Typography>
              </Box>
              <Box sx={{ p: 1.15, borderRadius: 1.25, bgcolor: 'action.hover' }}>
                <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.45, color: 'text.primary' }}>
                  {feedback.summary ||
                    feedback.description ||
                    'Please review requested edits below.'}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mt: 2, borderRadius: 2.5, boxShadow: PAGE_CARD_SHADOW }}>
            <Typography sx={{ fontWeight: 600, mb: 1.25, fontSize: '1.15rem', lineHeight: 1.2 }}>
              Requested Edits
            </Typography>
            <Box sx={{ display: 'grid', gap: 3.5 }}>
              {feedbackItems.length ? (
                feedbackItems.map((item, index) => {
                  const severityColor = getSeverityColor(item?.severity);
                  const lines = toMessageLines(item?.message);
                  const fieldLabel =
                    FIELD_PREVIEW_CONFIG[toCanonicalFieldKey(item?.field)]?.label ??
                    toDisplayFieldName(item?.field);
                  return (
                    <Box
                      key={`${item?.field || 'field'}-${index}`}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        // border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                        borderLeft: `4px solid ${severityColor.rail}`,
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.75 }}
                      >
                        <Typography sx={{ fontWeight: 500, fontSize: '1.1rem', lineHeight: 1.2 }}>
                          {fieldLabel}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${toTitleCase(item?.severity || 'low')} Priority`}
                          sx={{
                            bgcolor: severityColor.badgeBg,
                            color: severityColor.badgeColor,
                            fontWeight: 700,
                            borderRadius: 999,
                            fontSize: '0.82rem',
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          color: severityColor.actionColor,
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          mb: 0.5,
                        }}
                      >
                        {severityColor.actionLabel}
                      </Typography>
                      {lines.length ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          {lines.map((line, lineIdx) => (
                            <Typography
                              key={`${lineIdx}-${line}`}
                              sx={{
                                color: 'text.primary',
                                fontSize: '0.95rem',
                                lineHeight: 1.45,
                                pl: /^[•*-]\s/.test(line) ? 1.25 : 0,
                                position: 'relative',
                              }}
                            >
                              {line}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography
                          sx={{ color: 'text.primary', fontSize: '0.95rem', lineHeight: 1.45 }}
                        >
                          No details provided.
                        </Typography>
                      )}
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No requested edits available.
                </Typography>
              )}
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mt: 2, borderRadius: 2.5, boxShadow: PAGE_CARD_SHADOW }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: 1.5,
                gap: { xs: 1.25, sm: 1 },
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '1.15rem', lineHeight: 1.2 }}>
                Event Preview &amp; Edit
              </Typography>
              <Button
                startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
                disabled={!previewFields.length}
                onClick={() => setIsPreviewEditable((prev) => !prev)}
                sx={{
                  textTransform: 'none',
                  color: BRAND_ORANGE,
                  borderRadius: 999,
                  bgcolor: 'primary.light',
                  border: '1px solid',
                  borderColor: 'primary.light',
                  fontWeight: 600,
                  px: 1.35,
                  py: 0.75,
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {isPreviewEditable ? 'Done Editing' : 'Edit Event Details'}
              </Button>
            </Box>

            <Box sx={{ display: 'grid', gap: 1.4 }}>
              {previewFields.length ? (
                previewFields.map((field) => {
                  const isDescriptionField = field.key === 'description';
                  const isOnlineMeetingField = field.key === 'onlineMeetingLink';
                  const isMultilineField = isDescriptionField;
                  const isNumericField = field.key === 'totalSeats';
                  const charLimit = FIELD_CHAR_LIMITS[field.key];
                  const currentValue = editableFieldValues[field.key] ?? '';

                  return (
                    <Box key={field.key}>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: 'text.primary',
                          fontWeight: 700,
                          mb: 0.75,
                        }}
                      >
                        {field.label}
                      </Typography>
                      <Box
                        sx={{
                          minHeight: isDescriptionField ? EVENT_DESCRIPTION_MIN_HEIGHT : 'auto',
                        }}
                      >
                        {isPreviewEditable ? (
                          <>
                            <TextField
                              fullWidth
                              type={isNumericField ? 'number' : 'text'}
                              multiline={isMultilineField}
                              minRows={isDescriptionField ? EVENT_DESCRIPTION_MIN_ROWS : undefined}
                              value={currentValue}
                              onChange={(e) => handleEditableFieldChange(field.key, e.target.value)}
                              inputProps={{
                                ...(field.key === 'totalSeats' ? { min: 1, step: 1 } : {}),
                                ...(charLimit != null ? { maxLength: charLimit } : {}),
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '10px',
                                  bgcolor: 'background.paper',
                                  fontSize: '0.875rem',
                                  '& fieldset': {
                                    borderColor: 'divider',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'text.disabled',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: BRAND_ORANGE,
                                    borderWidth: 1,
                                  },
                                },
                              }}
                            />
                            {charLimit != null ? (
                              <CharacterCount current={currentValue.length} max={charLimit} />
                            ) : null}
                          </>
                        ) : isDescriptionField ? (
                          <ScrollableContentWithHint
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: '10px',
                              px: 1.2,
                              py: 1.35,
                              color: 'text.primary',
                              fontSize: '0.875rem',
                              lineHeight: 1.45,
                              bgcolor: 'background.paper',
                              ...SCROLLABLE_DESCRIPTION_SX,
                            }}
                          >
                            {field.value}
                          </ScrollableContentWithHint>
                        ) : isOnlineMeetingField ? (
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: '10px',
                              px: 1.2,
                              py: 1.1,
                              bgcolor: 'background.paper',
                            }}
                          >
                            <OnlineEventLocationDisplay
                              meetingLink={field.value === '—' ? '' : field.value}
                              dense
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: '10px',
                              px: 1.2,
                              py: 0.95,
                              color: 'text.primary',
                              fontSize: '0.875rem',
                              lineHeight: 1.45,
                              bgcolor: 'background.paper',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {field.value}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No field-specific feedback available.
                </Typography>
              )}
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mt: 2, borderRadius: 2.5, boxShadow: PAGE_CARD_SHADOW }}>
            <Typography sx={{ fontWeight: 600, fontSize: '1.15rem', lineHeight: 1.2, mb: 1 }}>
              Ready to Resubmit?
            </Typography>
            <Typography
              sx={{ fontSize: '0.85rem', lineHeight: 1.45, color: 'text.primary', mb: 1.5 }}
            >
              Once you&apos;ve addressed all the required changes, click the button below to
              resubmit your event for final approval. Our team typically reviews resubmissions
              within 24 hours.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              disabled={submitEventMutation.isPending}
              onClick={handleResubmitForApproval}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                borderRadius: 1.2,
                minHeight: 48,
                bgcolor: 'primary.main',
                boxShadow: 'none',
                '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
              }}
            >
              {submitEventMutation.isPending ? 'Resubmitting...' : 'Resubmit for Approval'}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <EventModerationTimeline
            event={data}
            title="Approval Timeline"
            showCompletedTimeline={false}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PartnerModerationFeedback;
